import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

// Interface for Learning Center data
interface LearningCenter {
  id: number;
  name: string;
}

@Component({
  selector: 'app-lc-selector-modal',
  templateUrl: './lc-selector-modal.component.html',
  styleUrls: ['./lc-selector-modal.component.scss']
})
export class LcSelectorModalComponent implements OnInit {
  allLearningCenters: LearningCenter[];
  filteredLearningCenters: LearningCenter[];
  searchTerm: string = '';
  searchText$ = new Subject<string>();

  constructor(
    public dialogRef: MatDialogRef<LcSelectorModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { users: LearningCenter[] } // Data passed to the modal
  ) {
    this.allLearningCenters = data.users.sort((a, b) => a.name.localeCompare(b.name)); // Sort LCs alphabetically by name
    this.filteredLearningCenters = [...this.allLearningCenters]; // Start with all LCs
  }

  ngOnInit(): void {
    // Debounce and distinctUntilChanged for search input to prevent excessive filtering
    this.searchText$.pipe(
      debounceTime(300), // Wait for 300ms after last keystroke
      distinctUntilChanged() // Only emit if the value is different from the last
    ).subscribe(term => {
      this.searchTerm = term;
      this.filterLearningCenters();
    });
  }

  /**
   * Filters the list of learning centers based on the search term.
   */
  filterLearningCenters(): void {
    if (!this.searchTerm) {
      this.filteredLearningCenters = [...this.allLearningCenters];
    } else {
      this.filteredLearningCenters = this.allLearningCenters.filter(lc =>
        lc.name.toLowerCase().includes(this.searchTerm.toLowerCase())
      );
    }
  }

  /**
   * Handles the selection of a learning center.
   * Closes the modal and passes the selected network ID back to the calling component.
   * @param networkId The ID of the selected learning center.
   */
  selectNetwork(networkId: number): void {
    this.dialogRef.close(networkId);
  }

  /**
   * Clears the search input and resets the filtered list.
   */
  clearSearch(): void {
    this.searchTerm = '';
    this.filterLearningCenters();
  }

  /**
   * Handles the cancel action.
   * Closes the modal without returning a selection.
   */
  onCancel(): void {
    this.dialogRef.close();
  }
}
